import { MODULE_TYPE_LABELS } from "entities/modules/constants";
import { PageModuleDataWithPageId, Page } from "entities/pages/types";
import { MouseEvent, useMemo, useState } from "react";
import { FilterOption, FilterProps } from "shared/components/Filter";
import { useAnchorEl } from "shared/hooks/useAnchorEl";

type FilterKey = "page" | "moduleType";

interface FilterConfig {
  key: FilterKey;
  label: string;
  sortAlphabetically?: boolean;
}

export interface ScheduleFilterHook {
  filtersData: FilterProps[];
  filteredModules: PageModuleDataWithPageId[];
  searchValue: string;
  setSearchValue: (value: string) => void;
}

const filterConfigs: FilterConfig[] = [
  {
    key: "page",
    label: "Page",
    sortAlphabetically: true,
  },
  {
    key: "moduleType",
    label: "Module Type",
    sortAlphabetically: true,
  },
];

export const useScheduleFilter = (
  modules: PageModuleDataWithPageId[],
  pages: Page[],
): ScheduleFilterHook => {
  const [selectedValues, setSelectedValues] = useState<{
    [key in FilterKey]?: FilterOption[];
  }>({});
  const [openStates, setOpenStates] = useState<{
    [key in FilterKey]?: boolean;
  }>({});
  const { anchorEl, handleAnchorClose, handleAnchorOpen } = useAnchorEl();
  const [searchValue, setSearchValue] = useState<string>("");

  const filterOptions = useMemo(() => {
    const pageMap = new Map<string, string>();
    modules.forEach((module) => {
      if (module.pageId) {
        const page = pages.find((p) => p.id === module.pageId);
        if (page) {
          pageMap.set(module.pageId, page.title);
        } else {
          pageMap.set(module.pageId, module.pageId);
        }
      }
    });

    const pageOptions = Array.from(pageMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const typeSet = new Set<string>();
    modules.forEach((module) => {
      if (module.type) {
        typeSet.add(module.type);
      }
    });

    const typeOptions = Array.from(typeSet)
      .filter((type) => Object.keys(MODULE_TYPE_LABELS).includes(type))
      .map((type) => ({
        value: type,
        label: MODULE_TYPE_LABELS[type as keyof typeof MODULE_TYPE_LABELS],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return {
      page: pageOptions,
      moduleType: typeOptions,
    };
  }, [modules, pages]);

  const filtersData: FilterProps[] = filterConfigs.map(
    ({ key, label, sortAlphabetically }) => {
      const options = filterOptions[key] || [];

      const handleChangeValue = (
        _: MouseEvent<HTMLElement>,
        option: FilterOption,
      ) => {
        setSelectedValues((prev) => {
          const currentOptions = prev[key] || [];
          const isOptionSelected = currentOptions.some(
            (o) => o.value === option.value,
          );

          const newValues = isOptionSelected
            ? currentOptions.filter((o) => o.value !== option.value)
            : [...currentOptions, option];

          return {
            ...prev,
            [key]: newValues,
          };
        });
      };

      const onClearSelectedValues = () =>
        setSelectedValues((prev) => ({ ...prev, [key]: [] }));

      const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setOpenStates((prev) => ({ ...prev, [key]: true }));
        handleAnchorOpen(event);
      };

      const handleClose = () => {
        setOpenStates((prev) => ({ ...prev, [key]: false }));
        handleAnchorClose();
      };

      return {
        title: label,
        options,
        anchorEl,
        open: openStates[key] || false,
        selectedValue: selectedValues[key] || [],
        handleOpenMenu,
        handleChangeValue,
        handleClose,
        onClearSelectedValues,
        disabled: false,
        sortAlphabetically,
      };
    },
  );

  const filteredModules = useMemo(() => {
    return modules.filter((module) => {
      const selectedPages = selectedValues.page || [];
      if (
        selectedPages.length > 0 &&
        !selectedPages.some((page) => page.value === module.pageId)
      ) {
        return false;
      }

      const selectedTypes = selectedValues.moduleType || [];
      if (
        selectedTypes.length > 0 &&
        !selectedTypes.some((type) => type.value === module.type)
      ) {
        return false;
      }

      if (
        searchValue &&
        !module.name?.toLowerCase().includes(searchValue.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [modules, selectedValues, searchValue]);

  return {
    filtersData,
    filteredModules,
    searchValue,
    setSearchValue,
  };
};
